import * as React from 'react';
import { Cross, ArrowDown } from '@sravni/react-icons';
import { useStyles, useFormControlContext, useBoolean, useStateCallback, isDefined, isFunction, useKeyPress, useMergedRef } from '@sravni/react-utils';
import { Dropdown } from '../../Dropdown/src/Dropdown.js';
import '../../Space/src/Space.js';
import '../../Textarea/src/Textarea.js';
import '../../Typography/src/Heading.js';
import '../../Typography/src/Link.js';
import '../../Typography/src/ListItem.js';
import '../../Typography/src/OrderedList.js';
import '../../Typography/src/Text.js';
import '../../Typography/src/UnorderedList.js';
import { useOptionsMap } from '../hooks/useOptionsMap.js';
import modules_b8cc600d from '../styles/Autocomplete.module.less.js';
import { EmptyContent } from './EmptyContent.js';
import { InputView } from './InputView.js';

const MIN_ROWS = 1;
// TODO мобильный вид требует доработки
const mobile = false;
/**
 * @description The Autocomplete component allow user to type and select from a list of options
 *
 * @component
 * @example
 * ```jsx
 * <Autocomplete placeholder="Услуги" defaultValue={value} options={options} />
 * ```
 */
// eslint-disable-next-line max-statements
const Autocomplete = React.forwardRef((props, ref) => {
    const { defaultValue, value, options, autoFocus, invalid, disabled, className, menuClassName, mode, icon, readOnly, placement, fixedWidth, resizable, emptyContent, title, closeOnSelect = true, flip = true, enableCustomValue, optionsSort = true, onChange, onSelect, onSearch, onClear, onFocus, onBlur, ...restProps } = props;
    const cx = useStyles(modules_b8cc600d);
    const isFirstRender = React.useRef(true);
    const containerRef = React.useRef(null);
    const inputRef = React.useRef(null);
    const menuRef = React.useRef(null);
    const hasUserInput = React.useRef(false);
    const formControlContext = useFormControlContext();
    const [visible, setVisible] = useBoolean(false);
    const [focused, setFocused] = useBoolean(false);
    const [fixWidth, setFixWidth] = useBoolean(fixedWidth);
    const [innerValue, setInnerValue] = useStateCallback(value ?? defaultValue);
    const [inputValue, setInputValue] = React.useState('');
    const [innerOptions, setInnerOptions] = React.useState(options || []);
    const [selected, setSelected] = React.useState(false);
    const getOptionByValue = useOptionsMap(options);
    const isMultiple = mode === 'multiple';
    const displayValue = React.useMemo(() => {
        if (isMultiple) {
            if (visible && !selected)
                return inputValue;
            if (!Array.isArray(innerValue) || !innerValue.length)
                return '';
            return innerValue
                .map((v) => getOptionByValue(v)?.label)
                .filter(Boolean)
                .join(', ');
        }
        if (visible) {
            return inputValue;
        }
        const { label } = getOptionByValue(innerValue);
        const newLabel = enableCustomValue && label !== inputValue ? inputValue : label;
        return newLabel;
    }, [isMultiple, enableCustomValue, selected, visible, inputValue, innerValue, getOptionByValue]);
    const setValue = React.useCallback((newValue) => {
        setInnerValue(newValue, (val) => {
            let label = '';
            if (!isMultiple) {
                label = getOptionByValue(val).label;
                setInputValue(label || val);
            }
            else {
                label = Array.isArray(val) ? val.map((v) => getOptionByValue(v)?.label).join(', ') : '';
            }
            onChange?.(label ?? '');
            onSelect?.(val ?? '');
        });
    }, [isMultiple, setInnerValue, getOptionByValue, onSelect, onChange]);
    const closeMenuOnSelect = React.useCallback((value) => (isDefined(closeOnSelect) && isFunction(closeOnSelect) ? closeOnSelect(value) : closeOnSelect), [closeOnSelect]);
    const handleSelect = React.useCallback((value) => {
        setValue(value);
        isMultiple && setSelected(true);
        !isMultiple && closeMenuOnSelect(value) && setVisible.off();
    }, [isMultiple, closeMenuOnSelect, setValue, setVisible]);
    const handleClick = React.useCallback(() => {
        inputRef.current?.focus();
    }, []);
    const handleChangeInput = React.useCallback((event) => {
        const { value } = event.currentTarget;
        setInputValue(value);
        if (onSearch === undefined) {
            setInnerOptions(options.filter((o) => o.label.toLowerCase().indexOf(value.toLowerCase()) >= 0));
        }
        hasUserInput.current = true;
        onChange?.(value);
        onSearch?.(value);
        !visible && inputRef.current?.click();
    }, [options, visible, onChange, onSearch]);
    const handleFocusInput = React.useCallback((event) => {
        if (!focused) {
            const { label } = getOptionByValue(innerValue);
            setFocused.on();
            if (!enableCustomValue) {
                setInputValue(label);
            }
            onFocus?.(event);
        }
    }, [focused, enableCustomValue, innerValue, getOptionByValue, onFocus, setFocused]);
    const handleBlurInput = React.useCallback((event) => {
        if (menuRef.current?.contains(event.relatedTarget)) {
            return;
        }
        const { label } = getOptionByValue(innerValue);
        if (enableCustomValue && label !== inputValue) {
            setInnerValue(undefined);
        }
        onBlur?.(event);
        setVisible.off();
        setFocused.off();
    }, [enableCustomValue, inputValue, innerValue, onBlur, getOptionByValue, setFocused, setInnerValue, setVisible]);
    const handleClickClear = React.useCallback((event) => {
        event.stopPropagation();
        setValue('');
        onClear?.();
        inputRef.current?.focus();
    }, [setValue, onClear]);
    const handleEmptyContentClick = React.useCallback(() => {
        inputRef.current?.focus();
    }, []);
    const handleClose = React.useCallback(() => {
        setVisible.off();
    }, [setVisible]);
    const iconElement = React.useMemo(() => {
        if (visible && isDefined(innerValue) && innerValue !== '') {
            return React.createElement(Cross, { className: cx('icon'), onClick: handleClickClear });
        }
        if (icon || icon === null) {
            return icon;
        }
        return (React.createElement(ArrowDown, { className: cx('icon', {
                [`is-arrow-turned`]: visible,
            }) }));
    }, [cx, visible, innerValue, icon, handleClickClear]);
    const header = React.useMemo(() => {
        {
            return null;
        }
    }, [title, displayValue, iconElement, resizable, handleChangeInput]);
    useKeyPress({
        // закрывает выпадающее меню по Enter в случае с произвольным вводом
        enabled: !!enableCustomValue && (visible || focused) && !isMultiple && !innerOptions.length,
        keyMap: {
            Enter: (event) => {
                event.preventDefault();
                closeMenuOnSelect(innerValue) && inputRef.current?.click();
            },
        },
    }, [innerValue, innerOptions.length, visible, focused, enableCustomValue, isMultiple, closeMenuOnSelect]);
    // defaultValue needs only on first render
    React.useEffect(() => {
        let label = '';
        if (isFirstRender.current) {
            label = getOptionByValue(value ?? defaultValue).label;
            isFirstRender.current = false;
            setInnerValue(value ?? defaultValue);
        }
        else {
            label = getOptionByValue(value).label;
            setInnerValue(value);
        }
        if (enableCustomValue) {
            setInputValue((prevVal) => label ?? value ?? prevVal);
        }
        else {
            setInputValue((prevVal) => label ?? prevVal);
        }
    }, [value, defaultValue, enableCustomValue, getOptionByValue, setInnerValue, setInputValue]);
    React.useEffect(() => {
        autoFocus && inputRef.current?.focus();
    }, [autoFocus]);
    React.useEffect(() => {
        if (!visible) {
            hasUserInput.current = false;
            setSelected(false);
        }
    }, [visible]);
    React.useEffect(() => {
        setInnerOptions(options || []);
    }, [options]);
    React.useEffect(() => {
        if (hasUserInput.current)
            setFixWidth.on();
    }, [setFixWidth]);
    return (React.createElement(Dropdown, { ref: menuRef, options: innerOptions, value: innerValue, 
        // eslint-disable-next-line @typescript-eslint/naming-convention
        className: cx(menuClassName, { 'hide-menu': !hasUserInput.current && !innerOptions.length }), mode: mode, placement: placement, disabled: disabled, readonly: readOnly, matchWidth: fixWidth, mobile: mobile, header: header, closeOnSelect: closeOnSelect, flip: flip, optionsSort: optionsSort, emptyContent: hasUserInput.current ? React.createElement(EmptyContent, { emptyContent: emptyContent, onClick: handleEmptyContentClick }) : null, tabIndex: -1, onOpen: setVisible.on, onClose: handleClose, onChange: handleSelect, onClick: handleClick },
        React.createElement("div", { ref: containerRef, "data-qa": "Autocomplete", className: cx(className, 'container'), tabIndex: -1 },
            React.createElement(InputView, { ...restProps, ref: useMergedRef(ref, inputRef), 
                // eslint-disable-next-line @typescript-eslint/naming-convention
                className: cx('textarea', { 'with-icon': iconElement && !resizable, focus: focused || visible }), readOnly: readOnly || mobile || selected || (!visible && isMultiple), invalid: formControlContext.invalid || invalid, disabled: formControlContext.disabled || disabled, value: displayValue, icon: iconElement, resizable: resizable, rows: MIN_ROWS, wrap: resizable ? 'soft' : 'off', onChange: handleChangeInput, onFocus: handleFocusInput, onBlur: handleBlurInput }))));
});
Autocomplete.defaultProps = {
    fixedWidth: true,
    placement: 'bottom-start',
    resizable: false,
    closeOnSelect: true,
    optionsSort: true,
};
Autocomplete.displayName = 'Autocomplete';

export { Autocomplete };
//# sourceMappingURL=Autocomplete.js.map
